import React, { useState, useRef, useMemo } from "react";
import SearchInputBox from "Components/common/SearchInputBox";

// Timezone-specialized fork of the calendar SingleSelectDropdown:
// - pins the most-used timezones at the top ("Common timezones")
// - search matches abbreviations and country words (EST, IST, india, uk...)
//   in addition to the backend display label and the IANA id.

const TZ_ALIASES = [
  { tokens: "est edt eastern us", match: /eastern|new[_ ]york/i },
  { tokens: "pst pdt pacific us", match: /pacific|los[_ ]angeles/i },
  { tokens: "cst cdt central us", match: /central (standard )?time|chicago/i },
  { tokens: "mst mdt mountain us", match: /mountain|denver/i },
  {
    tokens: "ist india indian delhi mumbai",
    // \b keeps "India" from matching the US "Indiana" entries
    match: /kolkata|calcutta|india\b/i
  },
  { tokens: "gmt bst uk britain england london", match: /london/i },
  {
    tokens: "cet cest europe germany france berlin paris",
    match: /berlin|paris|amsterdam|madrid|rome|vienna|brussels|w\.? europe|central europe/i
  },
  { tokens: "jst japan tokyo", match: /tokyo|japan/i },
  { tokens: "sgt singapore", match: /singapore/i },
  { tokens: "hkt hong kong", match: /hong[_ ]kong/i },
  { tokens: "aest aedt sydney australia", match: /sydney|melbourne|australia/i },
  { tokens: "gst uae dubai gulf", match: /dubai|abu[_ ]dhabi|gulf/i },
  { tokens: "brt brazil sao paulo", match: /sao[_ ]paulo|brasilia|brazil/i }
];

const PINNED_MATCHERS = [
  /new[_ ]york|eastern (standard )?time/i,
  /los[_ ]angeles|pacific (standard )?time/i,
  /chicago|central (standard )?time/i,
  /london/i,
  /kolkata|calcutta|india standard/i
];

// Backend labels non-negative offsets without a sign — "(GMT0:00)",
// "(GMT5:30)". Display-only fix; the stored displayName stays raw.
const formatTzLabel = (label = "") => label.replace(/\(GMT(\d)/i, "(GMT+$1");

const buildHaystack = (option = {}) => {
  const base = `${option?.displayName || ""} ${option?.name || ""}`
    .replace(/[_/]/g, " ")
    .toLowerCase();
  const aliasTokens = TZ_ALIASES.filter(alias => alias.match.test(base))
    .map(alias => alias.tokens)
    .join(" ");
  return `${base} ${aliasTokens}`
    .replace(/[^a-z0-9:+\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
};

// Exact word beats prefix beats substring, so "india" ranks India Standard
// Time above the US Indiana towns. A token with no match disqualifies.
const scoreOption = (words = [], tokens = []) => {
  let total = 0;
  for (const token of tokens) {
    let best = 0;
    for (const word of words) {
      if (word === token) {
        best = 3;
        break;
      }
      if (word.startsWith(token)) {
        best = Math.max(best, 2);
      } else if (word.includes(token)) {
        best = Math.max(best, 1);
      }
    }
    if (!best) {
      return 0;
    }
    total += best;
  }
  return total;
};

const TimezoneSelectDropdown = (props = {}) => {
  const {
    id = "",
    text = "",
    mandatory = false,
    search = true,
    options = [],
    value = "",
    err = "",
    tabIndex = 0,
    change = () => {},
    disabled = false
  } = props || {};

  const searchBoxRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState("");
  const [clearSearch, setClearSearch] = useState(false);

  const indexedOptions = useMemo(
    () =>
      (options || []).map(option => ({
        ...option,
        _searchWords: buildHaystack(option)
      })),
    [options]
  );

  const pinnedOptions = useMemo(() => {
    const pinned = [];
    PINNED_MATCHERS.forEach(matcher => {
      const found = indexedOptions.find(
        option =>
          matcher.test(`${option?.name} ${option?.displayName}`) &&
          !pinned.some(item => item?.name === option?.name)
      );
      if (found) {
        pinned.push(found);
      }
    });
    return pinned;
  }, [indexedOptions]);

  const queryTokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const filteredOptions = queryTokens.length
    ? indexedOptions
        .map(option => ({
          option,
          score: scoreOption(option._searchWords, queryTokens)
        }))
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(item => item.option)
    : null;
  const restOptions = indexedOptions.filter(
    option => !pinnedOptions.some(item => item?.name === option?.name)
  );

  const toggleDropdown = () => {
    if (!disabled) {
      const nextValue = !showDropdown;
      setShowDropdown(nextValue);
      if (nextValue && search && searchBoxRef?.current) {
        searchBoxRef.current.focus();
      }
    }
  };

  const closeDropdown = () => {
    setShowDropdown(false);
    setQuery("");
    setClearSearch(true);
  };

  const handleSelect = (option = {}) => {
    change(id, option?.name, option?.displayName);
    closeDropdown();
  };

  const renderOption = (option = {}) => (
    <li
      key={option?.name}
      className={`option ${value === option?.displayName ? "selected" : ""}`}
      title={formatTzLabel(option?.displayName)}
      onClick={() => handleSelect(option)}
    >
      {formatTzLabel(option?.displayName)}
    </li>
  );

  return (
    <div
      className={`singleSelectDropdown timezoneSelectDropdown ${
        disabled ? "disabled" : ""
      }`}
      id={id}
      tabIndex={tabIndex}
    >
      <label
        className={`selectOption ${value ? "selectedValue" : ""} ${
          showDropdown ? "activeDropdown" : ""
        }`}
        onClick={toggleDropdown}
      >
        <div className="selectLabel">
          <div className="labelName">
            {text}
            {mandatory && <span className="required">*</span>}
          </div>
          <div className="activeValue" title={formatTzLabel(value)}>
            {formatTzLabel(value)}
          </div>
        </div>
        <i className="material-icons-outlined arrowUpDown">
          {showDropdown ? "expand_less" : "expand_more"}
        </i>
        {showDropdown && <span className="focusBorder" />}
      </label>
      {showDropdown && (
        <div className="dropdownOptions animatedFast">
          {search && (
            <SearchInputBox
              valueCbk={setQuery}
              type="text"
              placeholder="Search by city, country or EST, IST..."
              searchBoxRef={searchBoxRef}
              clearSearchInput={clearSearch}
            />
          )}
          <ul className="selectOptionDropdown">
            {filteredOptions ? (
              filteredOptions.length ? (
                filteredOptions.map(renderOption)
              ) : (
                <div className="emptyOption">No timezones found</div>
              )
            ) : (
              <>
                {pinnedOptions.length > 0 && (
                  <>
                    <div className="tzGroupLabel">Common timezones</div>
                    {pinnedOptions.map(renderOption)}
                    <div className="tzGroupLabel">All timezones</div>
                  </>
                )}
                {restOptions.map(renderOption)}
              </>
            )}
          </ul>
        </div>
      )}
      {showDropdown && (
        <div className="selectOptionOverlay" onClick={closeDropdown} />
      )}
      {err && <span className="errMsgText">{err}</span>}
    </div>
  );
};

export default TimezoneSelectDropdown;
export { TimezoneSelectDropdown };
